import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';

// Form control
import { UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntypedFormControl, FormGroupDirective, NgForm, Validators, AbstractControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';

// Angular material components
import { MatTabsModule, MatTabChangeEvent } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';

// Custom services
import { ControllerService } from '../../../services/controller/controller.service';
import { LanguageService } from '../../../services/language/language.service';
import { UtilsService } from '../../../services/utils/utils.service';

// Custom components
import { WaitingPopupComponent } from '../../popups/waiting-popup/waiting-popup.component';

// Constant configuration values
import { configurationValues } from '../../../constants/global';

/* Error when invalid control is dirty, touched, or submitted (input fields) */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}


@Component({
  selector: 'app-corporate-users',
  imports: [
    // Angular modules
    MatTabsModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatListModule,
    MatDividerModule,
    // Custom modules
    WaitingPopupComponent,
  ],
  templateUrl: './corporate-users.component.html',
  styleUrl: './corporate-users.component.css'
})
export class CorporateUsersComponent {

  // Temporary variable to store domain while displaying the decision popup
  domainToBeManaged:string = ''

  // Waiting popup info
  displayWaitingPopup = false
  waitingMessage = ''

  // Confirmation popup info
  confirmationPopupData:any = null

  // Menu and list values
  //  New domain
  result_password:string = ''
  optionMenu_Region_value_NewDomain:string = ''
  optionMenu_LanguageCode_value_NewDomain:string = ''
  optionMenu_DbCollaborator_value_NewDomain:boolean|null = null
  optionMenu_EmailAndPasswordAuth_value_NewDomain:boolean|null = null
  optionMenu_PasswordRotation_value_NewDomain:number|null = null
  optionMenu_Region_OptionList = configurationValues.regionList
  optionMenu_LanguageCode_OptionList = configurationValues.languageList
  optionMenu_DbCollaborator_OptionList = configurationValues.dbCollaboratorOptions
  optionMenu_PasswordRotation_OptionList = configurationValues.passwordRotationList
  optionMenu_EmailAndPasswordAuth_OptionList = configurationValues.emailAndPasswordAuthOptions
  //  Existing domains
  optionMenue_Region_value_ExistingDomains:string = ''
  list_ExistingDomains = []





  // Form controls
  formControl_adminEmail = new UntypedFormControl('', [
    Validators.required,
    Validators.email,
    this.spacesInStringValidators.bind(this),
  ]); 
  formControl_notificationEmail = new UntypedFormControl('', [
    Validators.required,
    Validators.email,
    this.spacesInStringValidators.bind(this),
  ]); 
  formControl_nDays = new UntypedFormControl('', [
    Validators.required,
    Validators.pattern("^[0-9]*$"),
    Validators.min(1)               
  ]);
  // Form control group (need to keep this name cause its binded to parents form control)
  //    It needs to be created before constructing and initializing the view  
  formControlGroup = new UntypedFormGroup({
    adminEmail: this.formControl_adminEmail,
    notificationEmail: this.formControl_notificationEmail,
    nDays: this.formControl_nDays,
  }, {});
  // Error matcher
  matcher = new MyErrorStateMatcher();

  // Constructor
  constructor(
    public controllerService:ControllerService,
    public languageService:LanguageService,
    public utilsService:UtilsService
  ){}


  // ----- Tabs events ----- //
  onTabChanged(event: MatTabChangeEvent) {
    if (event.index === 0) {
      this.result_password = ''
      this.optionMenu_Region_value_NewDomain = ''
      this.optionMenu_LanguageCode_value_NewDomain = ''
      this.optionMenu_DbCollaborator_value_NewDomain = null
      this.optionMenu_PasswordRotation_value_NewDomain = null
    } else if (event.index === 1) {
      // Rest view
      this.optionMenue_Region_value_ExistingDomains = ''
      this.list_ExistingDomains = []
    }
  }



  // ----- Action buttons (New Domain) ----- //
  optionMenuClick_Region_NewDomain(item:any){
    this.optionMenu_Region_value_NewDomain = item
  }
  optionMenuClick_LanguageCode_NewDomain(item:any){
    this.optionMenu_LanguageCode_value_NewDomain = item
  }
  optionMenuClick_DbCollaborator_NewDomain(item:any){
    this.optionMenu_DbCollaborator_value_NewDomain = item
  }
  getDbCollaborationOptionString(isCollaborator:boolean|null){
    if(isCollaborator===true) return this.languageService.Strings.messages.boolean_yes
    else if(isCollaborator===false) return this.languageService.Strings.messages.boolean_no
    else return ''
  }
  optionMenuClick_EmailAndPasswordAuth_NewDomain(item:any){
    this.optionMenu_EmailAndPasswordAuth_value_NewDomain = item
    // Update password rotation policy accordingly 
    if(item == false) this.optionMenu_PasswordRotation_value_NewDomain = null
  }
  optionMenuClick_PasswordRotation_NewDomain(item:any){
    this.optionMenu_PasswordRotation_value_NewDomain = item
  }
  getEmailAndPasswordAuthOptionString(loginWithEmailAndPassword:boolean|null){
    if(loginWithEmailAndPassword===true) return this.languageService.Strings.messages.boolean_yes
    else if(loginWithEmailAndPassword===false) return this.languageService.Strings.messages.boolean_no
    else return ''
  }
  formIsValid(){
    // Verify that the region is correctly selected
    if(!this.optionMenu_Region_OptionList.includes(this.optionMenu_Region_value_NewDomain)){
      return false
    }
    // Verify that the language code is corrected selected
    if(!this.optionMenu_LanguageCode_OptionList.includes(this.optionMenu_LanguageCode_value_NewDomain)){
      return false
    }
    // Verify that the email (for admin and for notification) and the license duration are valid
    if(!this.formControl_adminEmail.valid || !this.formControl_notificationEmail.valid || !this.formControl_nDays.valid){
      return false
    }
    // Verify that the db collaborator option is specified
    if(!this.optionMenu_DbCollaborator_OptionList.includes(this.optionMenu_DbCollaborator_value_NewDomain ?? undefined as any)){
      return false
    }  
    // Verify that the auth method is specified
    if(!this.optionMenu_EmailAndPasswordAuth_OptionList.includes(this.optionMenu_EmailAndPasswordAuth_value_NewDomain ?? undefined as any)){
      return false
    }
    // Verify that the password rotation configuration is specified (only if email and password auth is set to true)
    if(this.optionMenu_EmailAndPasswordAuth_value_NewDomain === true &&
      !this.optionMenu_PasswordRotation_OptionList.includes(this.optionMenu_PasswordRotation_value_NewDomain ?? undefined as any)){
      return false
    } 
    // Return true otherwise
    return true
  }

  async buttonClicked_craeteLicense(){
    // Display the waiting popup
    this.waitingMessage = this.languageService.Strings.messages.info_creatingLicense
    this.displayWaitingPopup = true
    // Carry out the request and wait for the result
    const adminEmail = this.formControl_adminEmail.value
    const notificationEmail = this.formControl_notificationEmail.value
    const nDays = this.formControl_nDays.value
    const region = this.optionMenu_Region_value_NewDomain
    const languageCode = this.optionMenu_LanguageCode_value_NewDomain
    const dbCollaborator = this.optionMenu_DbCollaborator_value_NewDomain!
    const emailPasswordAuth = this.optionMenu_EmailAndPasswordAuth_value_NewDomain!
    const passwordRotation = this.optionMenu_PasswordRotation_value_NewDomain! ?? 0 // If null, set to 0
    const resultDict = await this.controllerService.newDomain_corporate(
      adminEmail, region, languageCode, nDays, dbCollaborator, notificationEmail, emailPasswordAuth, passwordRotation)
    // Stop the waiting popup
    this.displayWaitingPopup = false
    // Manage errors
    if(!resultDict['success']){
      // TODO: Manage specific errors
      return this.utilsService.displayErrorPopup(this.languageService.Strings.errors.action_unableToCreateLicense)
    }
    // Display success popup
    this.utilsService.displaySuccessPopup(this.languageService.Strings.messages.info_licenseCreated)
  }







  // ----- Action buttons (Existing Domains) ----- //
  async optionMenuClick_Region_ExistingDomains(region:string){
    // Display the waiting popup
    this.waitingMessage = this.languageService.Strings.messages.info_gettingLicenses
    this.displayWaitingPopup = true
    // Carry out the request and wait for the result
    const resultDict = await this.controllerService.listDomains_corporate(region)
    // Stop the waiting popup
    this.displayWaitingPopup = false
    // Manage errors
    if(!resultDict['success']){
      // TODO: Manage specific errors
      return this.utilsService.displayErrorPopup(this.languageService.Strings.errors.action_unableToGetLicenses)
    }
    // Manage success
    this.optionMenue_Region_value_ExistingDomains = region
    this.list_ExistingDomains = resultDict['result']
  }

  async buttonClicked_domainSettings(domain: string) {
    // Display the waiting popup
    this.waitingMessage = this.languageService.Strings.messages.info_gettingLicenses
    this.displayWaitingPopup = true
    // Carry out the request and wait for the result
    const region = this.optionMenue_Region_value_ExistingDomains
    const resultDict = await this.controllerService.getDomain_corporate(region, domain)
    // Stop the waiting popup
    this.displayWaitingPopup = false
    // Manage errors
    if(!resultDict['success']){
      // Manage specific errors
      return this.utilsService.displayErrorPopup(this.languageService.Strings.errors.action_unableToGetLicenseInformation)
    }
    // Manage result
    const licenseData = resultDict['result']
    // Display the popup
    this.utilsService.displayCorporateLicensePopup(region, domain, licenseData)
  }




  async buttonClicked_deleteDomain(domain: string) {
    // Store the domain to be deleted
    this.domainToBeManaged = domain
    //  Ask to store the changes
    const title = this.languageService.Strings.messages.pupupTitle_dangerousAction
    const message = this.languageService.Strings.messages.pupupText_dangerousAction_deleteDomain
    this.utilsService.displayDecisionPopup(title, message, this.buttonClicked_deleteDomain_Callback.bind(this))
  }

  async buttonClicked_deleteDomain_Callback(shouldDeleteDomain:boolean){
    // If the domain should not be deleted
    if(!shouldDeleteDomain){
      this.domainToBeManaged = ''
      return 
    }
    // If the domain should be deleted...
    const domain = this.domainToBeManaged
    // Reset the domain value
    this.domainToBeManaged = ''
    // Display the waiting popup
    this.waitingMessage = this.languageService.Strings.messages.info_gettingLicenses
    this.displayWaitingPopup = true
    // Carry out the request and wait for the result
    const region = this.optionMenue_Region_value_ExistingDomains
    const resultDict = await this.controllerService.deleteDomain_corporate(region, domain)
    // Stop the waiting popup
    this.displayWaitingPopup = false
    // Manage errors
    if(!resultDict['success']){
      // Manage specific errors
      return this.utilsService.displayErrorPopup(this.languageService.Strings.errors.action_unableToDeleteDomain)
    }
    // Update the local list
    this.list_ExistingDomains = this.list_ExistingDomains.filter(d => d !== domain);
    // Notify the user
    const infoMessage = this.languageService.Strings.messages.confirmation_domainRemoved
    return this.utilsService.displaySnackBar(infoMessage)
  }













  // ----- Custom validators ------ //

  specialCharactersValidators(control: AbstractControl) {
    //let addmittedCharacters = /^[a-zA-Z0-9_]*$/; // regular expression (solo símbolos a-z, A-Z, 0-9, _)
    let addmittedCharacters = /^[a-zA-Z0-9\t\n ./<>¿?;:"'`¡!@#$%^&*()\[\]{}_+=ºªñÑ,·çüÜ|\\-áéíóúÁÉÍÓÚ-]*$/; // regular expression
    let currentString = control.value
    if(currentString){
      if(currentString.length > 0){
        if(!addmittedCharacters.test(currentString)){
          return { specialCharactersValidationError: true };
        }
      }
    }
    return null;
  }
  spacesInStringValidators(control: AbstractControl) {
    let currentString = control.value
    if(currentString){
      if(currentString.length > 0){
        if(currentString.indexOf(' ') >= 0){
          return { spacesInStringValidationError: true };
        }
      }
    }
    return null;
  }

  // Method to be able to set custom errors comming from firebase into the input boxes to be used in errorMatcher
  //  example: 'firebaseError'
  // The dialog item requires an id
  displayErrorInDialogField(dialogItemId:string, errorType:string){
    var errorDict:any = {}
    errorDict[errorType] = true
    this.formControlGroup.controls[dialogItemId].setErrors(errorDict);
    // Example error dict: ('email', 'firebaseError')
  }

}
